import { Box, Paper, Stack, Typography } from '@mui/material';
import type { FC } from 'react';

import { SectionTitle } from '../../../components/analyticsUi';

import type {
    AssessmentCohort,
    AssessmentCompletionOverview,
    AssessmentDurationStats,
} from 'hooks/analytics';
import useTranslate from 'hooks/useTranslate';
import formatDuration from '../../../utils/formatDuration';
import assessmentCompletionColors from '../assessmentCompletionColors';

interface OverviewCardsProps {
    overview: AssessmentCompletionOverview;
    selectedAgent?: {
        name: string;
        stats: AssessmentDurationStats;
    };
}

const cohortCards: Array<{ cohort: AssessmentCohort; labelKey: string }> = [
    { cohort: 'combined', labelKey: 'assessmentCombined' },
    { cohort: 'selfAssessment', labelKey: 'assessmentSelfAssessments' },
    { cohort: 'agentAssisted', labelKey: 'assessmentAgentAssisted' },
    { cohort: 'withCv', labelKey: 'assessmentCvUploaded' },
    { cohort: 'withoutCv', labelKey: 'assessmentNoCvUploaded' },
];

const OverviewCards: FC<OverviewCardsProps> = ({ overview, selectedAgent }) => {
    const { translate } = useTranslate();

    return (
        <Box component="section" aria-labelledby="assessment-overview-title">
            <SectionTitle>
                <span id="assessment-overview-title">{translate('assessmentOverview')}</span>
            </SectionTitle>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, minmax(0, 1fr))',
                        lg: 'repeat(5, minmax(0, 1fr))',
                    },
                    gap: 1.25,
                    mb: 2,
                }}
            >
                {cohortCards.map(({ cohort, labelKey }) => {
                    const isAgentCohort = cohort === 'agentAssisted';
                    const stats =
                        isAgentCohort && selectedAgent ? selectedAgent.stats : overview[cohort];
                    // A cohort the provider has no journeys in is absent, not zero.
                    if (!stats) return null;
                    const color = assessmentCompletionColors[cohort];

                    return (
                        <Paper
                            key={cohort}
                            variant="outlined"
                            sx={{
                                borderRadius: 2,
                                borderColor: 'divider',
                                borderTop: `3px solid ${color}`,
                                p: 1.5,
                            }}
                        >
                            <Stack
                                direction="row"
                                sx={{ alignItems: 'center', justifyContent: 'space-between' }}
                            >
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                        {translate(labelKey)}
                                    </Typography>
                                    {isAgentCohort ? (
                                        <Typography color="text.secondary" sx={{ fontSize: 10 }}>
                                            {selectedAgent?.name ??
                                                translate('assessmentAllAgents')}
                                        </Typography>
                                    ) : null}
                                </Box>
                                <Typography
                                    color="text.secondary"
                                    sx={{ fontSize: 10, fontVariantNumeric: 'tabular-nums' }}
                                >
                                    n={stats.sampleSize}
                                </Typography>
                            </Stack>

                            <Typography color="text.secondary" sx={{ fontSize: 10, mt: 1 }}>
                                {translate('assessmentMedianTime')}
                            </Typography>
                            <Typography
                                sx={{
                                    color,
                                    fontSize: { xs: 24, sm: 27, lg: 24 },
                                    fontWeight: 800,
                                    letterSpacing: '-0.03em',
                                    lineHeight: 1.15,
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {stats.medianSeconds == null
                                    ? translate('assessmentNoDataValue')
                                    : formatDuration(stats.medianSeconds)}
                            </Typography>

                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                    gap: 0.75,
                                    mt: 1,
                                    pt: 1,
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                {[
                                    { labelKey: 'assessmentAverage', value: stats.avgSeconds },
                                    { labelKey: 'assessmentP75', value: stats.p75Seconds },
                                    { labelKey: 'assessmentP90', value: stats.p90Seconds },
                                ].map((metric) => (
                                    <Box key={metric.labelKey} sx={{ minWidth: 0 }}>
                                        <Typography color="text.secondary" sx={{ fontSize: 9 }}>
                                            {translate(metric.labelKey)}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                fontWeight: 700,
                                                fontVariantNumeric: 'tabular-nums',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {formatDuration(metric.value)}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    );
                })}
            </Box>
        </Box>
    );
};

export default OverviewCards;
